import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Calendar from "@/pages/Calendar";
import Vehicles from "@/pages/Vehicles";
import Drivers from "@/pages/Drivers";
import Tacho from "@/pages/Tacho";
import Maintenance from "@/pages/Maintenance";
import Office from "@/pages/Office";
import Invoices from "@/pages/Invoices";
import Operator from "@/pages/Operator";
import Reminders from "@/pages/Reminders";
import Team from "@/pages/Team";
import Admin from "@/pages/Admin";
import AcceptInvite from "@/pages/AcceptInvite";
import TrialSignup from "@/pages/TrialSignup";
import Landing from "@/pages/Landing";
import Platform from "@/pages/Platform";
import Demo from "@/pages/Demo";
import ResetPassword from "@/pages/ResetPassword";
import DriverApp from "@/pages/driver/DriverApp";
import Tracking from "@/pages/Tracking";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import Feedback from "@/pages/Feedback";
import Movements from "@/pages/Movements";
import Billing from "@/pages/Billing";
import TestMode from "@/pages/TestMode";
import ReviewInbox from "@/pages/ReviewInbox";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import DPA from "@/pages/DPA";
import CookieConsent from "@/components/CookieConsent";
import { Truck } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-400">
        <Truck className="animate-pulse" size={36} />
        <p className="text-sm tracking-widest uppercase">Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  const hideCookie = location.pathname.startsWith("/driver");

  return (
    <>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/platform" element={<Platform />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<Login />} />
      <Route path="/driver" element={<DriverApp />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/trial" element={<TrialSignup />} />
      <Route path="/invite" element={<TrialSignup />} />
      <Route path="/signup" element={<TrialSignup />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/dpa" element={<DPA />} />
      <Route path="/data-processing-agreement" element={<DPA />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/team" element={<Navigate to="/office?tab=team" replace />} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/operator" element={<Protected><Operator /></Protected>} />
      <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
      <Route path="/maintenance" element={<Protected><Maintenance /></Protected>} />
      <Route path="/inspections" element={<Navigate to="/maintenance" replace />} />
      <Route path="/vehicles" element={<Protected><Vehicles /></Protected>} />
      <Route path="/fleet" element={<Navigate to="/vehicles" replace />} />
      <Route path="/drivers" element={<Protected><Drivers /></Protected>} />
      <Route path="/tracking" element={<Protected><Tracking /></Protected>} />
      <Route path="/tacho" element={<Protected><Tacho /></Protected>} />
      <Route path="/office" element={<Protected><Office /></Protected>} />
      <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
      <Route path="/training" element={<Navigate to="/office" replace />} />
      <Route path="/insurance" element={<Navigate to="/office" replace />} />
      <Route path="/defects" element={<Navigate to="/maintenance" replace />} />
      <Route path="/documents" element={<Navigate to="/office" replace />} />
      <Route path="/reminders" element={<Navigate to="/office?tab=reminders" replace />} />
      <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
      <Route path="/movements" element={<Protected><Movements /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/test-mode" element={<Protected><TestMode /></Protected>} />
      <Route path="/review-inbox" element={<Protected><ReviewInbox /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    {!hideCookie && <CookieConsent />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
